import { useCallback, useState, type Dispatch, type SetStateAction } from "react";
import { sessionUser } from "./sessionUser";

// useState that outlives the screen: what a screen showed — what was ticked, picked, filtered — is there again when you
// come back to it from another step, tab or screen (Mark, 23-10-2026: "in elke stap moet de selectie onthouden worden
// na overschakelen tussen schermen"). The first user is Plannen, under keys with the day in them, so another day starts
// clean and going back to a day finds it as left.
//
// Like lib/listState: in memory, and in this tab's sessionStorage to survive F5; under the user signed in
// (lib/sessionUser). A value must be JSON, or a Set of it. Only the last KEEP keys written are kept.

const KEY = "wlx:sessionState";
const KEEP = 300;
// How a Set is written down.
const SET = "~set";

const memory = new Map<string, unknown>();
let loadedFor: string | null | undefined;

function storageKey(user: string | null | undefined): string {
  return user ? `${KEY}:${user}` : KEY;
}

function load(): void {
  const user = sessionUser();
  if (loadedFor === user) return;
  loadedFor = user; // even on failure: a broken store must not be retried per render
  memory.clear();
  try {
    const raw = sessionStorage.getItem(storageKey(user));
    if (!raw) return;
    const entries = JSON.parse(raw, (_key, value: unknown) =>
      value && typeof value === "object" && !Array.isArray(value) && Array.isArray((value as Record<string, unknown>)[SET])
        ? new Set((value as Record<string, unknown[]>)[SET])
        : value,
    ) as [string, unknown][];
    for (const [key, value] of entries) memory.set(key, value);
  } catch {
    /* no storage (private mode) or unreadable — this tab starts fresh */
  }
}

function save(): void {
  try {
    sessionStorage.setItem(
      storageKey(loadedFor),
      JSON.stringify([...memory], (_key, value: unknown) => (value instanceof Set ? { [SET]: [...value] } : value)),
    );
  } catch {
    /* storage full or unavailable — the in-memory copy still covers this tab */
  }
}

export function readSessionState<T>(key: string): T | undefined {
  load();
  return memory.get(key) as T | undefined;
}

export function writeSessionState(key: string, value: unknown): void {
  load();
  // The last written goes to the end, the oldest out.
  memory.delete(key);
  memory.set(key, value);
  while (memory.size > KEEP) memory.delete(memory.keys().next().value!);
  save();
}

function resolve<T>(initial: T | (() => T)): T {
  return typeof initial === "function" ? (initial as () => T)() : initial;
}

/**
 * useState kept under `key` while the component is gone, and given back when it comes again. Another key (another
 * day) gives what that key holds, or `initial`. Without a key it is plain useState.
 */
export function useSessionState<T>(key: string | null, initial: T | (() => T)): [T, Dispatch<SetStateAction<T>>] {
  const read = (at: string | null): T => {
    if (at !== null) {
      load();
      if (memory.has(at)) return memory.get(at) as T;
    }
    return resolve(initial);
  };
  const [state, setState] = useState<{ key: string | null; value: T }>(() => ({ key, value: read(key) }));
  let current = state;
  if (state.key !== key) {
    current = { key, value: read(key) };
    setState(current);
  }
  const set = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    setState((previous) => {
      const value = typeof next === "function" ? (next as (value: T) => T)(previous.value) : next;
      if (previous.key !== null) writeSessionState(previous.key, value);
      return { key: previous.key, value };
    });
  }, []);
  return [current.value, set];
}
